<template>
	<div class="min-h-screen bg-gray-50 py-2 sm:py-4">
		<div class="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
			<!-- Header -->
			<div class="text-center mb-3 sm:mb-6">
				<div
					class="inline-flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 bg-blue-100 rounded-full mb-2 sm:mb-3"
				>
					<i class="fas fa-credit-card text-blue-600"></i>
				</div>
				<h1 class="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
					Complete Payment
				</h1>
				<p class="text-xs sm:text-sm text-gray-600 px-2 sm:px-0">
					Secure M-Pesa payment to activate your insurance policy
				</p>
			</div>

			<!-- Main Card -->
			<div
				class="bg-white rounded-lg sm:rounded-xl shadow-md sm:shadow-lg overflow-hidden"
			>
				<div class="lg:grid lg:grid-cols-2">
					<!-- Left Section - Payment Details -->
					<div
						class="bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-4 lg:p-6"
					>
						<div class="flex items-center mb-2 sm:mb-4">
							<div
								class="w-6 h-6 sm:w-8 sm:h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-2"
							>
								<i
									class="fas fa-file-invoice-dollar text-white"
								></i>
							</div>
							<h2
								class="text-lg sm:text-xl font-bold text-gray-900"
							>
								Payment Summary
							</h2>
						</div>

						<!-- Payment Details -->
						<div class="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
							<div
								class="flex items-center justify-between py-1.5 sm:py-2 border-b border-blue-200"
							>
								<div class="flex items-center">
									<i
										class="fas fa-box text-gray-500 mr-1.5"
									></i>
									<span
										class="text-xs sm:text-sm text-gray-700 font-medium"
										>Selected Product</span
									>
								</div>

								<span
									v-if="
										selectPurchaseType != 'recommendation'
									"
									class="text-sm sm:text-base text-gray-900 font-semibold truncate max-w-[120px] sm:max-w-none"
									>{{
										selectedProduct
											? selectedProduct.productName
											: 'N/A'
									}}</span
								>
								<span
									v-if="
										selectPurchaseType === 'recommendation'
									"
									class="text-sm sm:text-base text-gray-900 font-semibold truncate max-w-[120px] sm:max-w-none"
									>{{
										recommendedProduct
											? recommendedProduct.productName
											: 'N/A'
									}}</span
								>
							</div>

							<div
								class="flex items-center justify-between py-2 sm:py-3 border-b border-blue-200"
							>
								<div class="flex items-center">
									<i
										class="fas fa-money-bill-wave text-gray-500 mr-2"
									></i>
									<span
										class="text-sm sm:text-base text-gray-700 font-medium"
										>Premium Amount</span
									>
								</div>
								<span
									v-if="
										selectCalculatedQuote &&
										selectCalculatedQuote.totalPremium
									"
									class="text-lg sm:text-xl font-bold text-blue-600"
								>
									KES
									{{
										selectCalculatedQuote.totalPremium.toLocaleString() ||
										0
									}}
								</span>
								<span
									class="text-lg sm:text-xl font-bold text-blue-600"
									v-else="selectQuotationData"
								>
									{{ selectQuotationData.totalPremium || 0 }}
								</span>
							</div>

							<div
								class="flex items-center justify-between py-2 sm:py-3 border-b border-blue-200"
							>
								<div class="flex items-center">
									<i
										class="fas fa-shield-alt text-gray-500 mr-2"
									></i>
									<span
										class="text-sm sm:text-base text-gray-700 font-medium"
										>Cover Type</span
									>
								</div>
								<span
									v-if="
										selectQuotationData || selectedCoverType
									"
									class="text-gray-900 font-semibold"
									>{{
										selectedCoverType.name ||
										selectQuotationData.coverType
									}}</span
								>
							</div>

							<div
								class="flex items-center justify-between py-2 sm:py-3 border-b border-blue-200"
							>
								<div class="flex items-center">
									<i
										class="fas fa-calendar-alt text-gray-500 mr-2"
									></i>
									<span
										class="text-sm sm:text-base text-gray-700 font-medium"
										>Cover Period</span
									>
								</div>
								<span
									class="text-sm sm:text-base text-gray-900 font-semibold"
									>{{
										selectQuotationData
											? selectQuotationData.period +
												' Days'
											: 'N/A'
									}}</span
								>
							</div>
						</div>
					</div>

					<!-- Right Section - Payment Form -->
					<div class="p-4 sm:p-6 lg:p-8">
						<section v-if="paymentMethod == 'm-pesa'">
							<div class="flex items-center mb-3 sm:mb-4">
								<div
									class="w-6 h-6 sm:w-8 sm:h-8 bg-green-600 rounded-lg flex items-center justify-center mr-2"
								>
									<svg
										class="w-3 h-3 sm:w-4 sm:h-4 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
										/>
									</svg>
								</div>
								<h2
									class="text-lg sm:text-xl font-bold text-gray-900"
								>
									M-Pesa Payment
								</h2>
							</div>

							<!-- M-Pesa Number Input -->
							<div class="mb-3 sm:mb-4">
								<label
									for="mpesaNumber"
									class="block text-xs sm:text-sm font-semibold text-gray-700 mb-2"
								>
									M-Pesa Phone Number
								</label>
								<div class="relative">
									<div
										class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
									>
										<svg
											class="h-4 w-4 sm:h-5 sm:w-5 text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
											/>
										</svg>
										<span
											class="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500 font-medium"
											>+254</span
										>
									</div>
									<input
										type="tel"
										id="mpesaNumber"
										v-model="phoneInput"
										@input="formatPhoneNumber"
										placeholder=""
										maxlength="9"
										required
										class="block w-full pl-20 sm:pl-24 pr-3 py-2 sm:py-3 text-sm sm:text-base border-2 border-gray-200 rounded-lg shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-200 outline-none"
									/>
								</div>
								<p
									v-if="phoneError"
									class="mt-2 text-xs sm:text-sm text-red-600 flex items-center"
								>
									<svg
										class="w-3 h-3 sm:w-4 sm:h-4 mr-1"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
									{{ phoneError }}
								</p>
							</div>
							<!-- Payment Status -->
							<div class="mb-6 sm:mb-8">
								<div
									v-if="paymentStatus === 'pending'"
									class="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4"
								>
									<div class="flex items-center">
										<svg
											class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>

										<p
											class="text-xs sm:text-sm text-blue-800 font-medium"
										>
											Enter your M-Pesa number to receive
											payment prompt
										</p>
									</div>
								</div>

								<div
									v-else-if="paymentStatus === 'processing'"
									class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4"
								>
									<div class="flex items-center mb-1 sm:mb-2">
										<div
											class="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-yellow-600 mr-2"
										></div>
										<p
											class="text-xs sm:text-sm text-yellow-800 font-medium"
										>
											Check your phone for the M-Pesa
											prompt
										</p>
									</div>
									<p
										class="text-yellow-700 text-xs sm:text-sm ml-6 sm:ml-7"
									>
										Waiting for your confirmation...
									</p>
								</div>

								<div
									v-else-if="paymentStatus === 'failed'"
									class="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4"
								>
									<div class="flex items-center">
										<svg
											class="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
										<p
											class="text-xs sm:text-sm text-red-800 font-medium"
										>
											Payment Failed. Please try again.
										</p>
									</div>
								</div>

								<div
									v-else-if="paymentStatus === 'success'"
									class="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4"
								>
									<div class="flex items-center">
										<svg
											class="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M5 13l4 4L19 7"
											/>
										</svg>
										<p
											class="text-xs sm:text-sm text-green-800 font-medium"
										>
											Payment Successful! Your policy is
											being processed.
										</p>
									</div>
								</div>
							</div>
							<div class="flex my-3 gap-3 items-center">
								<input
									v-model="isTermsChecked"
									type="checkbox"
									@change="handleChecked"
									class="h-4 w-4"
								/>
								<span
									>I accept the
									<a
										class="font-semibold underline text-blue-700"
										target="_blank"
										href="/terms"
										>Terms, Privacy Policy</a
									></span
								>
							</div>
						</section>
						<section v-if="paymentMethod == 'card'" class="mt-3">
							<div
								class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
							>
								<!-- Compact Header -->
								<div class="bg-primary p-3 sm:p-4 text-white">
									<div
										class="flex items-center justify-between"
									>
										<div>
											<h3
												class="text-base sm:text-lg font-semibold"
											>
												Pay with Debit or Credit Card
											</h3>
										</div>
										<div class="flex gap-2">
											<img
												src="https://cdn-icons-png.flaticon.com/512/349/349221.png"
												alt="Visa"
												class="h-6 sm:h-8 opacity-90"
											/>
											<img
												src="https://cdn-icons-png.flaticon.com/512/349/349228.png"
												alt="Mastercard"
												class="h-6 sm:h-8 opacity-90"
											/>
										</div>
									</div>
								</div>

								<!-- Compact Form Content -->
								<div class="p-4 sm:p-6 space-y-4">
									<!-- Card Number -->
									<div>
										<label
											for="card-number"
											class="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5"
										>
											Card Number
										</label>
										<div class="relative">
											<input
												id="card-number"
												v-model="cardDetails.number"
												type="text"
												inputmode="numeric"
												maxlength="19"
												placeholder="1234 5678 9012 3456"
												required
												class="w-full px-3 py-2.5 pl-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm sm:text-base tracking-wider"
												style="
													padding-left: 64px !important;
												"
											/>
											<i
												class="absolute left-3 top-1/2 -translate-y-1/2 fas fa-credit-card text-gray-400"
											></i>
										</div>
									</div>

									<!-- Compact Expiry & CVV -->
									<div
										class="grid grid-cols-2 gap-3 sm:gap-4"
									>
										<div>
											<label
												for="expiry"
												class="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5"
											>
												Expiry (MM/YY)
											</label>
											<input
												id="expiry"
												v-model="cardDetails.expiry"
												type="text"
												placeholder="MM/YY"
												maxlength="5"
												required
												class="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm sm:text-base"
											/>
										</div>

										<div>
											<label
												for="cvv"
												class="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5"
											>
												CVV
											</label>
											<div class="relative">
												<input
													id="cvv"
													v-model="cardDetails.cvv"
													type="password"
													inputmode="numeric"
													maxlength="4"
													placeholder="•••"
													required
													class="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm sm:text-base"
												/>
												<i
													class="absolute right-3 top-1/2 -translate-y-1/2 fas fa-info-circle text-gray-400"
												></i>
											</div>
										</div>
									</div>

									<!-- Cardholder Name -->
									<div>
										<label
											for="cardholder"
											class="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5"
										>
											Cardholder Name
										</label>
										<input
											id="cardholder"
											v-model="cardDetails.name"
											type="text"
											placeholder="JOHN DOE"
											required
											class="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm sm:text-base uppercase"
										/>
									</div>

									<!-- Compact Security Info -->
									<div class="pt-3 border-t border-gray-200">
										<div
											class="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-4"
										>
											<div
												class="flex items-center gap-2"
											>
												<i
													class="fas fa-check-circle text-green-600"
												></i>
												<span
													>Secure encrypted
													payment</span
												>
											</div>
											<img
												src="https://www.npci.org.in/sites/default/files/2021-09/rupay-logo.png"
												alt="Rupay"
												class="h-5 sm:h-7"
											/>
										</div>
									</div>
								</div>
							</div>

							<!-- Trust badges -->
							<!-- <div class="mt-6 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
    <span>🔒 256-bit SSL Encryption</span>
    <span>•</span>
    <span>Protected by PCI DSS</span>
    <span>•</span>
    <span>Verified by Visa • Mastercard SecureCode</span>
  </div> -->
						</section>
						<section
							v-if="paymentMethod === 'bank'"
							class="space-y-6"
						>
							<!-- Header -->
							<div class="flex items-center gap-3">
								<div
									class="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center"
								>
									<svg
										class="w-5 h-5 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 10h18M5 6h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"
										/>
									</svg>
								</div>
								<h2 class="text-2xl font-bold text-gray-900">
									Bank Payment
								</h2>
							</div>

							<!-- Info Notice -->
							<div
								class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800"
							>
								Please make a bank transfer and fill in the
								details below to confirm your payment.
							</div>

							<!-- Form -->
							<div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
								<!-- Bank Name -->
								<div>
									<label
										class="block text-sm font-semibold text-gray-700 mb-2"
										>Bank Name</label
									>
									<input
										v-model="bankForm.bankName"
										type="text"
										placeholder="e.g. Equity Bank"
										class="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
									/>
								</div>

								<!-- Account Name -->
								<div>
									<label
										class="block text-sm font-semibold text-gray-700 mb-2"
										>Account Name</label
									>
									<input
										v-model="bankForm.accountName"
										type="text"
										placeholder="Name used during payment"
										class="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
									/>
								</div>

								<!-- Account Number -->
								<div>
									<label
										class="block text-sm font-semibold text-gray-700 mb-2"
										>Account Number</label
									>
									<input
										v-model="bankForm.accountNumber"
										type="text"
										placeholder="••••1234"
										class="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
									/>
								</div>

								<!-- Transaction Reference -->
								<div>
									<label
										class="block text-sm font-semibold text-gray-700 mb-2"
									>
										Transaction Reference
										<span class="text-red-500">*</span>
									</label>
									<input
										v-model="bankForm.reference"
										type="text"
										placeholder="Bank reference / narration"
										class="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
									/>
								</div>

								<!-- Amount -->
								<div>
									<label
										class="block text-sm font-semibold text-gray-700 mb-2"
										>Amount Paid</label
									>
									<input
										v-model="bankForm.amount"
										type="number"
										disabled
										class="w-full p-3 border rounded-lg bg-gray-100"
									/>
								</div>

								<!-- Payment Date -->
								<div>
									<label
										class="block text-sm font-semibold text-gray-700 mb-2"
										>Payment Date</label
									>
									<input
										v-model="bankForm.paymentDate"
										type="date"
										class="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
									/>
								</div>
							</div>

							<!-- Terms -->
							<div class="flex items-center gap-2">
								<input
									v-model="isTermsChecked"
									type="checkbox"
									class="h-4 w-4"
								/>
								<span class="text-sm">
									I accept the
									<a
										href="/terms"
										target="_blank"
										class="font-semibold text-blue-700 underline"
									>
										Terms & Privacy Policy
									</a>
								</span>
							</div>

							<!-- Status -->
							<div
								v-if="paymentStatus === 'processing'"
								class="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
							>
								<p class="text-sm text-yellow-800 font-medium">
									Payment submitted. Awaiting verification.
								</p>
							</div>

							<div
								v-if="paymentStatus === 'success'"
								class="bg-green-50 border border-green-200 rounded-lg p-4"
							>
								<p class="text-sm text-green-800 font-medium">
									Payment received. Your order is being
									processed.
								</p>
							</div>

							<div
								v-if="paymentStatus === 'failed'"
								class="bg-red-50 border border-red-200 rounded-lg p-4"
							>
								<p class="text-sm text-red-800 font-medium">
									We could not verify your payment. Please
									contact support.
								</p>
							</div>
						</section>

						<!-- Action Buttons -->
						<div class="flex flex-col gap-3 sm:gap-4">
							<!-- Main action buttons row -->
							<div
								class="flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-4 my-3"
							>
								<button
									@click="initiatePayment"
									:disabled="
										paymentStatus === 'processing' ||
										!isValidPhoneNumber ||
										!isTermsChecked
									"
									:class="[
										'order-1 sm:order-2 flex items-center justify-center px-6 py-2.5 text-sm sm:text-base text-white font-semibold rounded-lg transition-all duration-300 shadow-lg',
										isTermsChecked &&
										isValidPhoneNumber &&
										paymentStatus === 'pending'
											? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl transform hover:-translate-y-0.5'
											: 'bg-gray-400 cursor-not-allowed',
									]"
								>
									<svg
										v-if="paymentStatus === 'processing'"
										class="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											class="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											stroke-width="4"
										></circle>
										<path
											class="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									<svg
										v-else
										class="w-4 h-4 sm:w-5 sm:h-5 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
										/>
									</svg>
									{{
										paymentStatus === 'processing'
											? 'Processing...'
											: 'Pay Now'
									}}
								</button>

								<button
									type="button"
									@click="previous"
									class="order-2 sm:order-1 flex items-center justify-center px-4 py-2 text-sm sm:text-base text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
								>
									<svg
										class="w-4 h-4 sm:w-5 sm:h-5 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M15 19l-7-7 7-7"
										/>
									</svg>
									Previous
								</button>
							</div>

							<!-- Payment Method Buttons -->
							<div
								class="border-t border-gray-200 pt-4 space-y-3"
							>
								<button
									v-if="paymentMethod != 'card'"
									@click="togglePaymentMethod('card')"
									class="w-full py-3.5 px-6 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
								>
									<span
										class="flex items-center justify-center gap-3"
									>
										<svg
											class="w-7 h-7"
											viewBox="0 0 24 24"
											fill="currentColor"
										>
											<svg
												viewBox="0 0 24 24"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
												stroke="#FFFFFFF"
											>
												<g
													id="SVGRepo_bgCarrier"
													stroke-width="0"
												></g>
												<g
													id="SVGRepo_tracerCarrier"
													stroke-linecap="round"
													stroke-linejoin="round"
												></g>
												<g id="SVGRepo_iconCarrier">
													<g opacity="0.5">
														<path
															d="M21.9995 12.8175L21.591 12.409C20.7123 11.5303 19.2877 11.5303 18.409 12.409L17.6076 13.2104C17.2878 12.3573 16.4648 11.75 15.5 11.75C14.2574 11.75 13.25 12.7574 13.25 14V15.7638C12.7601 15.8183 12.2847 16.0334 11.909 16.409C11.0303 17.2877 11.0303 18.7123 11.909 19.591L12.318 20H10C6.22876 20 4.34315 20 3.17157 18.8284C2 17.6569 2 15.7712 2 12C2 11.5581 2.00188 10.392 2.00377 10H22C22.0019 10.392 22 11.5581 22 12C22 12.283 22 12.5553 21.9995 12.8175Z"
															fill="#ffffff"
														></path>
													</g>
													<path
														d="M5.25 16C5.25 15.5858 5.58579 15.25 6 15.25H10C10.4142 15.25 10.75 15.5858 10.75 16C10.75 16.4142 10.4142 16.75 10 16.75H6C5.58579 16.75 5.25 16.4142 5.25 16Z"
														fill="#ffffff"
													></path>
													<path
														d="M9.99484 4H14.0052C17.7861 4 19.6766 4 20.8512 5.11578C21.6969 5.91916 21.9337 7.07507 22 9V10H2V9C2.0663 7.07507 2.3031 5.91916 3.14881 5.11578C4.3234 4 6.21388 4 9.99484 4Z"
														fill="#ffffff"
													></path>
													<path
														d="M19.4697 13.4697C19.7626 13.1768 20.2374 13.1768 20.5303 13.4697L22.5303 15.4697C22.8232 15.7626 22.8232 16.2374 22.5303 16.5303C22.2374 16.8232 21.7626 16.8232 21.4697 16.5303L20.75 15.8107V20C20.75 20.4142 20.4142 20.75 20 20.75C19.5858 20.75 19.25 20.4142 19.25 20V15.8107L18.5303 16.5303C18.2374 16.8232 17.7626 16.8232 17.4697 16.5303C17.1768 16.2374 17.1768 15.7626 17.4697 15.4697L19.4697 13.4697Z"
														fill="#ffffff"
													></path>
													<path
														fill-rule="evenodd"
														clip-rule="evenodd"
														d="M15.5 13.25C15.9142 13.25 16.25 13.5858 16.25 14V18.1893L16.9697 17.4697C17.2626 17.1768 17.7374 17.1768 18.0303 17.4697C18.3232 17.7626 18.3232 18.2374 18.0303 18.5303L16.0303 20.5303C15.7374 20.8232 15.2626 20.8232 14.9697 20.5303L12.9697 18.5303C12.6768 18.2374 12.6768 17.7626 12.9697 17.4697C13.2626 17.1768 13.7374 17.1768 14.0303 17.4697L14.75 18.1893V14C14.75 13.5858 15.0858 13.25 15.5 13.25Z"
														fill="#ffffff"
													></path>
												</g>
											</svg>
										</svg>
										Pay with Card
									</span>
								</button>
								<button
									v-if="paymentMethod != 'm-pesa'"
									@click="togglePaymentMethod('m-pesa')"
									class="w-full py-3.5 px-6 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-green-600 to-green-700 rounded-xl shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500/50"
								>
									<span
										class="flex items-center justify-center gap-3"
									>
										<svg
											class="w-7 h-7"
											viewBox="0 0 24 24"
											fill="currentColor"
										>
											<svg
												viewBox="0 0 24 24"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
												stroke="#FFFFFFF"
											>
												<g
													id="SVGRepo_bgCarrier"
													stroke-width="0"
												></g>
												<g
													id="SVGRepo_tracerCarrier"
													stroke-linecap="round"
													stroke-linejoin="round"
												></g>
												<g id="SVGRepo_iconCarrier">
													<g opacity="0.5">
														<path
															d="M21.9995 12.8175L21.591 12.409C20.7123 11.5303 19.2877 11.5303 18.409 12.409L17.6076 13.2104C17.2878 12.3573 16.4648 11.75 15.5 11.75C14.2574 11.75 13.25 12.7574 13.25 14V15.7638C12.7601 15.8183 12.2847 16.0334 11.909 16.409C11.0303 17.2877 11.0303 18.7123 11.909 19.591L12.318 20H10C6.22876 20 4.34315 20 3.17157 18.8284C2 17.6569 2 15.7712 2 12C2 11.5581 2.00188 10.392 2.00377 10H22C22.0019 10.392 22 11.5581 22 12C22 12.283 22 12.5553 21.9995 12.8175Z"
															fill="#ffffff"
														></path>
													</g>
													<path
														d="M5.25 16C5.25 15.5858 5.58579 15.25 6 15.25H10C10.4142 15.25 10.75 15.5858 10.75 16C10.75 16.4142 10.4142 16.75 10 16.75H6C5.58579 16.75 5.25 16.4142 5.25 16Z"
														fill="#ffffff"
													></path>
													<path
														d="M9.99484 4H14.0052C17.7861 4 19.6766 4 20.8512 5.11578C21.6969 5.91916 21.9337 7.07507 22 9V10H2V9C2.0663 7.07507 2.3031 5.91916 3.14881 5.11578C4.3234 4 6.21388 4 9.99484 4Z"
														fill="#ffffff"
													></path>
													<path
														d="M19.4697 13.4697C19.7626 13.1768 20.2374 13.1768 20.5303 13.4697L22.5303 15.4697C22.8232 15.7626 22.8232 16.2374 22.5303 16.5303C22.2374 16.8232 21.7626 16.8232 21.4697 16.5303L20.75 15.8107V20C20.75 20.4142 20.4142 20.75 20 20.75C19.5858 20.75 19.25 20.4142 19.25 20V15.8107L18.5303 16.5303C18.2374 16.8232 17.7626 16.8232 17.4697 16.5303C17.1768 16.2374 17.1768 15.7626 17.4697 15.4697L19.4697 13.4697Z"
														fill="#ffffff"
													></path>
													<path
														fill-rule="evenodd"
														clip-rule="evenodd"
														d="M15.5 13.25C15.9142 13.25 16.25 13.5858 16.25 14V18.1893L16.9697 17.4697C17.2626 17.1768 17.7374 17.1768 18.0303 17.4697C18.3232 17.7626 18.3232 18.2374 18.0303 18.5303L16.0303 20.5303C15.7374 20.8232 15.2626 20.8232 14.9697 20.5303L12.9697 18.5303C12.6768 18.2374 12.6768 17.7626 12.9697 17.4697C13.2626 17.1768 13.7374 17.1768 14.0303 17.4697L14.75 18.1893V14C14.75 13.5858 15.0858 13.25 15.5 13.25Z"
														fill="#ffffff"
													></path>
												</g>
											</svg>
										</svg>
										M-Pesa Payment
									</span>
								</button>
								<button
									v-if="paymentMethod != 'bank'"
									@click="togglePaymentMethod('bank')"
									class="w-full py-3 px-6 text-md font-semibold text-white bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 ease-out focus:outline-none focus:ring-4 focus:ring-indigo-500/40"
								>
									<span
										class="flex items-center justify-center gap-3"
									>
										<svg
											class="w-7 h-7"
											viewBox="0 0 24 24"
											fill="currentColor"
										>
											<svg
												viewBox="0 0 24 24"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
												stroke="#FFFFFFF"
											>
												<g
													id="SVGRepo_bgCarrier"
													stroke-width="0"
												></g>
												<g
													id="SVGRepo_tracerCarrier"
													stroke-linecap="round"
													stroke-linejoin="round"
												></g>
												<g id="SVGRepo_iconCarrier">
													<g opacity="0.5">
														<path
															d="M21.9995 12.8175L21.591 12.409C20.7123 11.5303 19.2877 11.5303 18.409 12.409L17.6076 13.2104C17.2878 12.3573 16.4648 11.75 15.5 11.75C14.2574 11.75 13.25 12.7574 13.25 14V15.7638C12.7601 15.8183 12.2847 16.0334 11.909 16.409C11.0303 17.2877 11.0303 18.7123 11.909 19.591L12.318 20H10C6.22876 20 4.34315 20 3.17157 18.8284C2 17.6569 2 15.7712 2 12C2 11.5581 2.00188 10.392 2.00377 10H22C22.0019 10.392 22 11.5581 22 12C22 12.283 22 12.5553 21.9995 12.8175Z"
															fill="#ffffff"
														></path>
													</g>
													<path
														d="M5.25 16C5.25 15.5858 5.58579 15.25 6 15.25H10C10.4142 15.25 10.75 15.5858 10.75 16C10.75 16.4142 10.4142 16.75 10 16.75H6C5.58579 16.75 5.25 16.4142 5.25 16Z"
														fill="#ffffff"
													></path>
													<path
														d="M9.99484 4H14.0052C17.7861 4 19.6766 4 20.8512 5.11578C21.6969 5.91916 21.9337 7.07507 22 9V10H2V9C2.0663 7.07507 2.3031 5.91916 3.14881 5.11578C4.3234 4 6.21388 4 9.99484 4Z"
														fill="#ffffff"
													></path>
													<path
														d="M19.4697 13.4697C19.7626 13.1768 20.2374 13.1768 20.5303 13.4697L22.5303 15.4697C22.8232 15.7626 22.8232 16.2374 22.5303 16.5303C22.2374 16.8232 21.7626 16.8232 21.4697 16.5303L20.75 15.8107V20C20.75 20.4142 20.4142 20.75 20 20.75C19.5858 20.75 19.25 20.4142 19.25 20V15.8107L18.5303 16.5303C18.2374 16.8232 17.7626 16.8232 17.4697 16.5303C17.1768 16.2374 17.1768 15.7626 17.4697 15.4697L19.4697 13.4697Z"
														fill="#ffffff"
													></path>
													<path
														fill-rule="evenodd"
														clip-rule="evenodd"
														d="M15.5 13.25C15.9142 13.25 16.25 13.5858 16.25 14V18.1893L16.9697 17.4697C17.2626 17.1768 17.7374 17.1768 18.0303 17.4697C18.3232 17.7626 18.3232 18.2374 18.0303 18.5303L16.0303 20.5303C15.7374 20.8232 15.2626 20.8232 14.9697 20.5303L12.9697 18.5303C12.6768 18.2374 12.6768 17.7626 12.9697 17.4697C13.2626 17.1768 13.7374 17.1768 14.0303 17.4697L14.75 18.1893V14C14.75 13.5858 15.0858 13.25 15.5 13.25Z"
														fill="#ffffff"
													></path>
												</g>
											</svg>
										</svg>
										Bank Payment
									</span>
								</button>
								<button
									@click="togglePaymentMethod('ipf')"
									disabled
									class="w-full flex items-center justify-center px-5 py-3 text-sm sm:text-base text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 hover:border-indigo-300 transition-all duration-200 font-medium"
								>
									<svg
										class="w-4 h-4 sm:w-5 sm:h-5 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
									IPF
								</button>
								<button
									@click="payLater"
									class="w-full flex items-center justify-center px-5 py-3 text-sm sm:text-base text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 hover:border-indigo-300 transition-all duration-200 font-medium"
								>
									<svg
										class="w-4 h-4 sm:w-5 sm:h-5 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
									Pay Later
								</button>
							</div>
						</div>

						<!-- Security Note -->
						<div
							class="mt-6 sm:mt-8 bg-gray-50 rounded-lg p-3 sm:p-4"
						>
							<div class="flex items-start">
								<svg
									class="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
									/>
								</svg>
								<div>
									<p
										class="text-xs sm:text-sm font-medium text-gray-700 mb-1"
									>
										Secure Payment
									</p>
									<p class="text-xs text-gray-600">
										Your payment is protected by M-Pesa's
										secure payment system. Never share your
										M-Pesa PIN with anyone.
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { CheckPaymentDetails, RequestPayment } from '../../../api/insurance';

export default {
	name: 'Payment',
	data() {
		return {
			phoneInput: '',
			phoneError: '',
			isValidPhoneNumber: false,
			isTermsChecked: false,
			paymentMethod: 'm-pesa',
			cardDetails: {
				number: '',
				expiry: '',
				cvv: '',
				name: '',
			},
			isProcessing: false,
			bankForm: {
				amount: 0,
				paymentDate: '',
				reference: '',
				accountNumber: 0,
				accountName: '',
			},
		};
	},
	computed: {
		...mapState('insurance/motorPrivate', ['paymentStatus']),
		...mapGetters('quotation', [
			'selectCalculatedQuote',
			'selectQuotationData',
			'selectSystemQuotation',
			'selectInvoiceRef',
			'selectedQuotationReview',
		]),
		...mapGetters('product', ['selectedProduct', 'selectedCoverType']),

		...mapGetters('auth', ['selectPurchaseType']),

		recommendedProduct() {
			return this.selectedQuotationReview.product;
		},
	},
	methods: {
		...mapMutations('insurance/motorPrivate', ['SET_PAYMENT_STATUS']),
		...mapActions('quotation', ['setPaymentResult']),
		formatPhoneNumber() {
			this.phoneInput = this.phoneInput
				.replace(/^\+?254/, '')
				.replace(/\D/g, '');
			if (this.phoneInput.length === 9) {
				if (/^(7|1)\d{8}$/.test(this.phoneInput)) {
					this.phoneError = '';
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Valid Phone Number',
						message: 'Your phone number is valid.',
						duration: 3000,
					});
					this.isValidPhoneNumber = true;
				} else {
					this.phoneError =
						'Please enter a valid Safaricom number starting with 7 or 1';
					Event.$emit('notifications:show', {
						type: 'error',
						title: 'Invalid Phone Number',
						message: this.phoneError,
						duration: 3000,
					});
					this.isValidPhoneNumber = false;
				}
			} else {
				this.phoneError =
					this.phoneInput.length > 0
						? 'Phone number must be 9 digits'
						: '';
				this.isValidPhoneNumber = false;
			}
		},
		handleChecked() {
			if (this.isTermsChecked) {
				console.log('tnc accepted');
			}
		},
		paymentData() {
			return {
				invoiceSystemRef: this.selectInvoiceRef,
				invoicePaymentDetails: {
					paymentProvider: 'mpesa',
					params: {
						PhoneNumber: '254' + this.phoneInput,
					},
				},
			};
		},
		async pollPaymentStatus(paymentRef, retries = 6, interval = 5000) {
			for (let i = 0; i < retries; i++) {
				await new Promise((resolve) => setTimeout(resolve, interval));

				const response = await CheckPaymentDetails({
					paymentSystemRef: paymentRef,
				});
				console.log(`Poll #${i + 1}:`, response.data);

				if (
					response.data.paymentRequestStatus === 'success' ||
					response.data.paymentRequestStatus === 'failed'
				) {
					return response;
				}
			}

			return {
				status: false,
				data: {
					paymentRequestStatus: 'failed',
					statusMessage: 'Payment timeout',
				},
			};
		},

		async initiatePayment() {
			const payload = this.paymentData();
			console.log('Initiating payment with data:', payload);

			if (!this.isValidPhoneNumber) {
				this.phoneError = 'Invalid phone number';
				return;
			}

			this.SET_PAYMENT_STATUS('processing');

			try {
				const res = await RequestPayment(payload);
				console.log('Payment response:', res);

				if (!res || !res.status || !res.data.paymentRef) {
					throw new Error('Invalid API response');
				}

				const paymentRef = res.data.paymentRef;

				const response = await this.pollPaymentStatus(paymentRef);

				if (response && response.status && response.data) {
					const { paymentRequestStatus, statusMessage } =
						response.data;

					if (paymentRequestStatus === 'success') {
						this.SET_PAYMENT_STATUS('success');
						this.setPaymentResult(response.data);

						Event.$emit('notifications:show', {
							type: 'success',
							title: 'Payment Successful',
							message:
								'Your payment has been successfully processed. Login into your account to view your policy details.',
							duration: 3000,
						});

						this.$router.push('/newportal');
					} else {
						this.SET_PAYMENT_STATUS('failed');
						Event.$emit('notifications:show', {
							type: 'error',
							title: 'Payment Failed',
							message: statusMessage || 'No payment received',
							duration: 6000,
						});
					}
				}
			} catch (error) {
				console.error('Payment error:', error);

				this.SET_PAYMENT_STATUS('failed');
				this.phoneError = 'Payment failed. Please try again.';

				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Payment Error',
					message:
						error.response.data.errors ||
						error.message ||
						'An unexpected error occurred',
					duration: 5000,
				});

				setTimeout(() => {
					this.SET_PAYMENT_STATUS('processing');
				}, 3000);
			}
		},

		payLater() {
			this.$router.push({ name: 'NewPortalQuotations' });
		},
		togglePaymentMethod(method) {
			this.paymentMethod = method;
		},
		previous() {
			this.$router.push({ name: 'SummaryPage' });
		},
		resetPaymentState() {
			this.SET_PAYMENT_STATUS('pending');
			this.phoneInput = '';
			this.phoneError = '';
			this.isValidPhoneNumber = false;
			console.log('Reset paymentStatus to pending');
		},
	},
	mounted() {
		this.resetPaymentState();
		console.log('recc-product', this.recommendedProduct);
		console.log('quote-product', this.selectQuotationData);
	},
};
</script>

<style scoped>
/* Custom animations and responsive adjustments */
@media (max-width: 640px) {
	.lg\:grid-cols-2 {
		grid-template-columns: 1fr;
	}
}

/* Improved touch targets for mobile */
@media (max-width: 640px) {
	button,
	input {
		-webkit-tap-highlight-color: transparent;
	}

	input {
		font-size: 16px; /* Prevents zoom on iOS */
	}
}

/* Prevent horizontal scrolling */
@media (max-width: 640px) {
	.max-w-6xl {
		max-width: 100%;
	}
}
</style>
